import { Link } from 'react-router-dom';
import { Chrome, Smartphone, CreditCard, Settings, ArrowRight, Crown, Store, MessageCircle } from 'lucide-react';
import { useAuthStore } from '../../stores/auth-store';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { CHROME_EXTENSION_URL, IOS_APP_URL, ANDROID_APP_URL } from '../../constants/downloads';

export default function Account() {
  const { t } = useLanguage();
  const { user } = useAuthStore();

  // Fetch marketplace unread count
  const { data: unreadData } = useQuery({
    queryKey: ['marketplace-unread'],
    queryFn: () => api.getMarketplaceUnreadCount(),
    enabled: !!user,
  });
  const unreadCount = unreadData?.data?.unread_count || 0;

  const tierLabels: Record<string, string> = {
    free: t('account.tier.free', 'Free'),
    pro: t('account.tier.pro', 'Pro'),
    pro_plus: t('account.tier.proPlus', 'Pro+'),
  };

  const tierColors: Record<string, string> = {
    free: 'bg-gray-100 text-gray-700',
    pro: 'bg-primary-100 text-primary-700',
    pro_plus: 'bg-accent text-gray-900',
  };

  return (
    <div className="space-y-6">
      {/* Welcome card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('account.welcomeBack', 'Welcome back!')}</h1>
            <p className="mt-1 text-gray-600">{user?.email}</p>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${tierColors[user?.subscription_tier || 'free']}`}>
            {user?.subscription_tier !== 'free' && <Crown className="w-4 h-4" />}
            {tierLabels[user?.subscription_tier || 'free']}
          </div>
        </div>

        {user?.subscription_tier === 'free' && (
          <div className="mt-4 p-4 bg-primary-50 rounded-lg">
            <p className="text-sm text-primary-800">
              {t('account.upgradePrompt', 'Upgrade to Pro to track more products and get instant notifications.')}
            </p>
            <Link
              to="/account/subscription"
              className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              {t('account.viewPlans', 'View plans')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/account/marketplace"
          className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
        >
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Store className="w-6 h-6 text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900">{t('account.myListings', 'My Listings')}</h3>
            <p className="text-sm text-gray-600">{t('account.myListingsDesc', 'Sell your IKEA furniture locally')}</p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </Link>

        <Link
          to="/account/marketplace/messages"
          className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
        >
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 relative">
            <MessageCircle className="w-6 h-6 text-blue-600" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900">{t('account.messages', 'Messages')}</h3>
            <p className="text-sm text-gray-600">{t('account.messagesDesc', 'Marketplace conversations')}</p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </Link>

        <Link
          to="/account/subscription"
          className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
        >
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-6 h-6 text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900">{t('account.subscription', 'Subscription')}</h3>
            <p className="text-sm text-gray-600">{t('account.subscriptionDesc', 'Manage your plan and billing')}</p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </Link>

        <Link
          to="/account/settings"
          className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
        >
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Settings className="w-6 h-6 text-gray-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900">{t('account.settings', 'Settings')}</h3>
            <p className="text-sm text-gray-600">{t('account.settingsDesc', 'Account preferences and security')}</p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </Link>
      </div>

      {/* Get the apps */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('account.apps.title', 'Get the full experience')}
        </h2>
        <p className="text-gray-600 mb-6">
          {t('account.apps.description', 'Download our app or browser extension to track products, scan barcodes, and receive instant notifications.')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Chrome Extension */}
          <a
            href={CHROME_EXTENSION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all"
          >
            <Chrome className="w-8 h-8 text-primary-600" />
            <div>
              <div className="text-xs text-gray-500">{t('account.apps.addTo', 'Add to')}</div>
              <div className="font-semibold text-gray-900">{t('account.apps.chrome', 'Chrome')}</div>
            </div>
          </a>

          {/* iOS App */}
          <a
            href={IOS_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all"
          >
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            <div>
              <div className="text-xs text-gray-500">{t('account.apps.downloadOn', 'Download on')}</div>
              <div className="font-semibold text-gray-900">{t('account.apps.appStore', 'App Store')}</div>
            </div>
          </a>

          {/* Android App */}
          <a
            href={ANDROID_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all"
          >
            <Smartphone className="w-8 h-8 text-green-600" />
            <div>
              <div className="text-xs text-gray-500">{t('account.apps.getItOn', 'Get it on')}</div>
              <div className="font-semibold text-gray-900">{t('account.apps.googlePlay', 'Google Play')}</div>
            </div>
          </a>
        </div>
      </div>

      {/* Features info */}
      <div className="bg-gray-50 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 mb-3">{t('account.features.title', 'What you can do with the app')}</h3>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-primary-600 rounded-full"></span>
            {t('account.features.watchlist', 'Build your watchlist and track product availability')}
          </li>
          <li className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-primary-600 rounded-full"></span>
            {t('account.features.priceHistory', 'View price history charts and price drop alerts')}
          </li>
          <li className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-primary-600 rounded-full"></span>
            {t('account.features.scanBarcodes', 'Scan product barcodes in-store')}
          </li>
          <li className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-primary-600 rounded-full"></span>
            {t('account.features.shoppingLists', 'Create shopping lists and checklists')}
          </li>
          <li className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-primary-600 rounded-full"></span>
            {t('account.features.pushNotifications', 'Get push notifications for restocks and deals')}
          </li>
        </ul>
      </div>
    </div>
  );
}
